/**
 * Utility functions for the CreateTicket page
 * Adapted from Thaklis app utilities to use ThaklisApiService
 */

import {
  STAT_FIELDS,
  DOCUMENT_FIELD_PATTERNS,
  SPECIFIC_DOCUMENT_FIELDS,
  FIELD_CATEGORIES,
  CONTEXT_TYPES
} from './constants';

// === pxToRem ===
export const pxToRem = (n) => `${n / 16}rem`;

// === Field Pattern Utils ===
export const isDocumentField = (field) => {
  const fieldName = typeof field === 'string' ? field : (field?.name || field?.title || '');
  if (!fieldName) return false;
  if (SPECIFIC_DOCUMENT_FIELDS.includes(fieldName)) return true;
  const lowerFieldName = fieldName.toLowerCase();
  return DOCUMENT_FIELD_PATTERNS.some(pattern => lowerFieldName.includes(pattern));
};

export const getFieldCategory = (field) => {
  const fieldName = typeof field === 'string' ? field : (field?.name || field?.title || '');
  if (!fieldName) return FIELD_CATEGORIES.SERVICE_SPECIFIC;
  const lowerFieldName = fieldName.toLowerCase();

  if (DOCUMENT_FIELD_PATTERNS.some(p => lowerFieldName.includes(p))) return FIELD_CATEGORIES.DOCUMENTS;
  if (/name|gender|birth|dob|age|nationality/.test(lowerFieldName)) return FIELD_CATEGORIES.PERSONAL_INFO;
  if (/email|phone|mobile|contact/.test(lowerFieldName)) return FIELD_CATEGORIES.CONTACT_INFO;
  if (/address|city|state|country|zip|postal/.test(lowerFieldName)) return FIELD_CATEGORIES.ADDRESS;
  return FIELD_CATEGORIES.SERVICE_SPECIFIC;
};

export const getFieldType = (field) => {
  if (typeof field !== 'string' && field?.type) return field.type;
  const fieldName = typeof field === 'string' ? field : (field?.name || '');
  const lower = fieldName.toLowerCase();
  if (isDocumentField(fieldName)) return 'FILE';
  if (lower.includes('date') || lower.includes('dob') || lower.includes('birth')) return 'DATE';
  if (lower.includes('gender') || lower.includes('status') || lower.includes('type')) return 'SELECT';
  return 'TEXT';
};

// === Field Structure Utils ===
export const createFieldStructure = (serviceId) => ({
  serviceId,
  categories: {},
  fieldProperties: {},
  timestamp: Date.now(),
  version: 1
});

export const cloneFieldStructure = (structure) => {
  if (!structure) return null;
  return JSON.parse(JSON.stringify(structure));
};

export const addFieldToStructure = (structure, fieldName, fieldProps = {}) => {
  const newStructure = cloneFieldStructure(structure);
  const category = fieldProps.category || getFieldCategory(fieldName);
  const fieldType = fieldProps.type || getFieldType(fieldName);
  if (!newStructure.categories[category]) newStructure.categories[category] = [];
  if (!newStructure.categories[category].includes(fieldName)) {
    newStructure.categories[category].push(fieldName);
  }
  newStructure.fieldProperties[fieldName] = { ...fieldProps, type: fieldType, category };
  newStructure.version += 1;
  newStructure.timestamp = Date.now();
  return newStructure;
};

export const processFieldsIntoStructure = (structure, fields) => {
  if (!fields || !Array.isArray(fields)) return structure;
  let updatedStructure = cloneFieldStructure(structure);
  fields.forEach(field => {
    if (typeof field === 'string') {
      updatedStructure = addFieldToStructure(updatedStructure, field);
    } else {
      const fieldName = field.name || field.code;
      updatedStructure = addFieldToStructure(updatedStructure, fieldName, {
        ...field,
        title: field.title || field.displayName
      });
    }
  });
  return updatedStructure;
};

export const updateStructureForContext = (structure, context) => {
  if (!structure) return null;
  const newStructure = cloneFieldStructure(structure);
  if (context === CONTEXT_TYPES.EMPLOYEE_SELECTED) {
    const documentFields = Object.keys(newStructure.fieldProperties)
      .filter(fn => {
        const props = newStructure.fieldProperties[fn];
        return props.type === 'FILE' || props.category === 'DOCUMENTS';
      });
    documentFields.forEach(fn => {
      Object.keys(newStructure.categories).forEach(cat => {
        newStructure.categories[cat] = newStructure.categories[cat].filter(f => f !== fn);
      });
      if (!newStructure.categories.DOCUMENTS) newStructure.categories.DOCUMENTS = [];
      if (!newStructure.categories.DOCUMENTS.includes(fn)) {
        newStructure.categories.DOCUMENTS.push(fn);
      }
      newStructure.fieldProperties[fn] = { ...newStructure.fieldProperties[fn], category: 'DOCUMENTS' };
    });
  }
  return newStructure;
};

// Singleton storage
let currentFieldStructure = null;
let currentContext = CONTEXT_TYPES.DEFAULT;

export const storeFieldStructure = (structure) => {
  currentFieldStructure = cloneFieldStructure(structure);
};
export const getStoredFieldStructure = () => cloneFieldStructure(currentFieldStructure);
export const setCurrentContext = (context) => { currentContext = context; };
export const getCurrentContext = () => currentContext;

// === Service Selection Utils ===
// NOTE: handleServiceSelection is adapted to use ThaklisApiService
// It's called from the main component and passed ThaklisApiService directly

export const processServiceFields = (serviceFields) => {
  if (!serviceFields || !Array.isArray(serviceFields)) return [];
  return serviceFields.map(field => {
    const category = getFieldCategory(field);
    let fieldLabel = field.title || field.label;
    if (!fieldLabel) {
      const nameToUse = field.name || field.id;
      if (nameToUse && typeof nameToUse === 'string') {
        const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (uuidPattern.test(nameToUse)) {
          fieldLabel = field.code || createFieldLabel(field.code) || 'Service Field';
        } else {
          fieldLabel = createFieldLabel(nameToUse);
        }
      } else {
        fieldLabel = 'Service Field';
      }
    }
    return {
      ...field,
      name: field.name || field.id,
      label: fieldLabel,
      title: fieldLabel,
      description: field.description || '',
      category,
      value: field.value || null,
      isServiceField: true
    };
  });
};

export const processStaticFields = (staticFields) => {
  if (!staticFields || !Array.isArray(staticFields)) return [];
  return staticFields.map(fieldName => {
    let fieldType = 'TEXT';
    if (isDocumentField(fieldName)) fieldType = 'FILE';
    const category = getFieldCategory(fieldName);
    const fieldLabel = createFieldLabel(fieldName);
    return {
      id: fieldName,
      name: fieldName,
      label: fieldLabel,
      type: fieldType,
      category,
      isStatic: true,
      required: true,
      value: null
    };
  });
};

export const combineFieldsWithoutDuplicates = (serviceFields, staticFields) => {
  const allFieldNames = new Set();
  const uniqueServiceFields = [];
  for (const field of serviceFields) {
    if (field && field.name) {
      allFieldNames.add(field.name.toLowerCase());
      uniqueServiceFields.push(field);
    }
  }
  const uniqueStaticFields = staticFields.filter(field => {
    if (!field || !field.name) return false;
    const lower = field.name.toLowerCase();
    if (allFieldNames.has(lower)) return false;
    allFieldNames.add(lower);
    return true;
  });
  return { allFields: [...uniqueServiceFields, ...uniqueStaticFields], uniqueServiceFields };
};

export const createFieldLabel = (fieldName) => {
  if (!fieldName) return '';
  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .replace(/Ar$/, ' (Arabic)')
    .replace(/En$/, ' (English)');
};

// === Employee Context Utils ===
export const handleEmployeeSelectionUtil = (
  employee, formRef, setFields, setUid, uidReady, setPopulatedFields, setSelectedEmployee
) => {
  setSelectedEmployee(employee);
  setCurrentContext(CONTEXT_TYPES.EMPLOYEE_SELECTED);
  const storedStructure = getStoredFieldStructure();
  if (!storedStructure) return null;
  const updatedStructure = updateStructureForContext(storedStructure, CONTEXT_TYPES.EMPLOYEE_SELECTED);

  if (employee.id) formRef.current?.setFieldValue('employeeId', employee.id);
  if (employee.uid) {
    formRef.current?.setFieldValue('uid', employee.uid);
    formRef.current?.setFieldValue('employeeUid', employee.uid);
    formRef.current?.setFieldValue('employee', employee);
    setUid(employee.uid);
    uidReady(true);
  }
  return updatedStructure;
};

// === Field Categorization ===
export const ensureConsistentFieldCategorization = (fields) => {
  if (!fields || !Array.isArray(fields)) return fields;
  return fields.map(field => {
    const updatedField = { ...field };
    if (isDocFieldByStatFields(field)) {
      updatedField.type = 'DOCUMENT';
      updatedField.category = 'DOCUMENTS';
    }
    return updatedField;
  });
};

const isDocFieldByStatFields = (field) => {
  if (!field) return false;
  if (field.type === 'DOCUMENT' || field.type === 'FILE') return true;
  if (field.name && STAT_FIELDS[field.name]) {
    return STAT_FIELDS[field.name].type === 'FILE';
  }
  const documentFieldNames = ['passportCopy', 'visaPage', 'birthCertificate', 'salaryCertificate', 'labourContract', 'photo', 'shinasnama'];
  if (field.name && documentFieldNames.includes(field.name)) return true;
  const documentKeywords = ['file', 'document', 'certificate', 'upload', 'photo', 'signature', 'copy', 'scan', 'passport'];
  if (field.name && documentKeywords.some(kw => field.name.toLowerCase().includes(kw))) return true;
  return false;
};

// Helper to convert camelCase to snake_case for backend doc types
export const toBackendDocumentType = (t) => {
  if (!t) return null;
  switch (t) {
    case 'passportCopy': return 'passport_copy';
    case 'visaPage': return 'visa_page';
    case 'salaryCertificate': return 'salary_certificate';
    case 'birthCertificate': return 'birth_certificate';
    case 'emiratesId': return 'emirates_id';
    case 'photo': return 'photo';
    case 'shinasnama': return 'shinasnama';
    case 'labourContract': return 'labour_contract';
    default:
      return String(t).replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`).toLowerCase();
  }
};
